import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AddBranchCard from "@/components/Subscribtion/AddBranchCard";
import PlanCard from "@/components/Subscribtion/PlanCard";
import SubscribtionExpireBadge from "@/components/Subscribtion/SubscribtionExpireBadge";
import MoyaserModal from "@/components/Subscribtion/MoyaserModal";
import { Skeleton } from "@/components/ui/skeleton";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector } from "react-redux";
import { AddBranchInformation, PlanCardType } from "@/types/globalTypes";
import ErrorMessage from "@/components/ErrorMessage";
import PaymentStatus from "@/components/Subscribtion/PaymentStatus";

export type MoyaserPaymentMethods = "creditcard" | "applepay" | null;

export default function Subscription() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  // Data
  const [plans, setPlans] = useState<PlanCardType[]>([]);
  const [addBranchInformation, setAddBranchInformation] =
    useState<AddBranchInformation>({} as AddBranchInformation);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);

  // Moyasar
  const [paymentData, setPaymentData] = useState<string | null>(null);
  const [ModalTitle, setModalTitle] = useState<string | null>(null);
  const [isMoyasarFormOpen, setIsMoyasarFormOpen] = useState(false);


  const onCloseMoyaserModal = () => {
    setIsMoyasarFormOpen(false);
    setPaymentData(null);
  };

  const handleRequestSubscription = async (
    planId: string,
    ModalTitle: string,
  ) => {
    setIsRequesting(true);
    const url = `${BASE_URL}/api/dashboard/settings/premium/subscribe?locale=${lang}`;
    try {
      const response = await axios.post(
        url,
        {
          planId,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (!response.data.success) {
        toast.error(
          response.data.error || (t("errors.unexpectedError") as string),
        );

        return;
      }

      setModalTitle(ModalTitle);
      setPaymentData(response.data.paymentData);
      setIsMoyasarFormOpen(true);
      // setModalFor("subscribe");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const ErrorMsg =
          error.response?.data?.error ||
          (t("errors.unexpectedError") as string);
        toast.error(ErrorMsg);
      } else {
        toast.error(t("errors.unexpectedError") as string);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRequestBranches = async (branchesNumber: number) => {
    setIsRequesting(true);
    const url = `${BASE_URL}/api/dashboard/branches/increase-limit?locale=${lang}`;
    try {
      const response = await axios.post(
        url,
        {
          count: branchesNumber,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (!response.data.success) {
        toast.error(
          response.data.error || (t("errors.unexpectedError") as string),
        );

        return;
      }

      setModalTitle(
        `${t("subscribtionSettings.addBranch")} - ${t("subscribtionSettings.RequestedBranches") + ` ( ${branchesNumber} )`}`,
      );
      setPaymentData(response.data.paymentData);
      setIsMoyasarFormOpen(true);
      // setModalFor("add-branch");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const ErrorMsg =
          error.response?.data?.error ||
          (t("errors.unexpectedError") as string);
        toast.error(ErrorMsg);
      } else {
        toast.error(t("errors.unexpectedError") as string);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    const fetchPlans = async () => {
      const url = `${BASE_URL}/api/dashboard/settings/premium/plans?locale=${lang}`;
      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: token,
          },
        });

        if (!response.data.success) {
          toast.error(
            response.data.error || (t("errors.unexpectedError") as string),
          );

          return;
        }
        setPlans(response.data.plans || []);
        setAddBranchInformation(response.data.branchesInformation || {});
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const ErrorMsg =
            error.response?.data?.error ||
            (t("errors.unexpectedError") as string);
          setError(ErrorMsg);
          toast.error(ErrorMsg);
        } else {
          setError(t("errors.unexpectedError") as string);
          toast.error(t("errors.unexpectedError") as string);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, [token, t, BASE_URL, lang]);

  if (isLoading)
    return (
      <>
        <div>
          <Skeleton className="mt-[3rem] h-[50px] w-full rounded-lg bg-gray-200 shadow-lg dark:bg-slate-600 dark:bg-opacity-35" />
        </div>

        <div className="mt-6 grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-[300px] w-full rounded-lg bg-gray-200 shadow-lg dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[300px] w-full rounded-lg bg-gray-200 shadow-lg dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[300px] w-full rounded-lg bg-gray-200 shadow-lg dark:bg-slate-600 dark:bg-opacity-35" />
        </div>
      </>
    );

  if (error) return <ErrorMessage msg={error} />;

  return (
    <>
      <div className="px-4">
        <h1 className="mb-5 self-start text-2xl font-semibold capitalize">
          {t("subscribtionSettings.manage")}
        </h1>

        {/* Subscribtion Expire Badge */}
        <SubscribtionExpireBadge />

        {/* Plans */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {plans?.map((card, i) => (
            <PlanCard
              key={card._id || i}
              card={card}
              isRequesting={isRequesting}
              handleRequestSubscription={handleRequestSubscription}
            />
          ))}

          {/* Add Branch */}
          <AddBranchCard
            addBranchInformation={addBranchInformation}
            handleRequestBranches={handleRequestBranches}
            isRequesting={isRequesting}
          />
        </div>

        {/* Moyasar */}
        {paymentData && isMoyasarFormOpen && (
            <MoyaserModal
              paymentData={paymentData}
              onCloseMoyaserModal={onCloseMoyaserModal}
              ModalTitle={ModalTitle}
            />
          )}
      </div>

      <PaymentStatus />
    </>
  );
}
